import type { EngineInput } from '@conciair/scheduler-core';
import { fetchSnapshot } from '../../engine/snapshot';
import { V1_HEADERS, type V1Tab } from './csv';

/**
 * Settings > Data > Export everything (spec 5.11): one CSV per input table
 * in the V1 tab layout (so it can go back into the Sheets tool or into this
 * importer) plus the engine-input JSON snapshot. Files download directly.
 */

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function toCsv(header: string[], rows: unknown[][]): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

function download(name: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const YN = (b: boolean | undefined) => (b === false ? 'N' : 'Y');

/** V1-shaped CSV rows for each tab, from the engine input. */
export function tabsFromInput(input: EngineInput): Partial<Record<V1Tab, unknown[][]>> {
  const name = new Map(input.pilots.map((p) => [p.id, `${p.lastName}, ${p.firstName}`]));
  const nm = (id: string | null | undefined) => (id ? name.get(id) ?? '' : '');
  return {
    AircraftTypes: input.aircraftTypes.map((t) => [t.type, t.swapDay]),
    Aircraft: input.aircraft.map((a) => [a.tail, a.type, YN(a.active)]),
    Pilots: input.pilots.map((p) => [p.id, p.lastName, p.firstName, p.startDate ?? '', p.terminationDate ?? '', YN(p.active), p.notes, p.preferredTail ?? '']),
    Qualifications: input.qualifications.map((q) => [q.pilotId, nm(q.pilotId), q.type, q.seat, YN(q.isTrainingCaptain), q.supervisedRemaining, YN(q.active), q.activeStart ?? '', q.activeEnd ?? '']),
    Unavailability: input.unavailability.map((u) => [u.pilotId, nm(u.pilotId), u.start, u.end, u.reason]),
    Overrides: input.overrides.map((o) => [o.kind, o.pilotId, nm(o.pilotId), o.tail ?? '', o.rotationStart, o.seat ?? '', o.days ?? '', o.notes]),
    Transitions: input.transitions.map((t) => [t.pilotId, nm(t.pilotId), t.changeType, t.effectiveDate, t.fromType, t.fromSeat, t.toType, t.toSeat, t.notes]),
    AircraftPairing: input.aircraftPairings.map((x) => [x.pilotId, nm(x.pilotId), x.tail, x.start ?? '', x.end ?? '', x.notes]),
    AlternatingPilots: input.alternating.map((x) => [x.pilotId1, nm(x.pilotId1), x.pilotId2, nm(x.pilotId2), x.start ?? '', x.end ?? '', x.notes]),
    PreviousRotation: (input.previousRotations ?? []).map((x) => [x.tail, x.picId ?? '', nm(x.picId), x.sicId ?? '', nm(x.sicId), x.start ?? '', x.notes]),
    Config: [
      ['report_start_date', input.reportStart, ''],
      ['horizon_months', input.horizonMonths, ''],
      ['weight_pairing_repeat', input.weights.pairingRepeat, ''],
      ['weight_tail_repeat', input.weights.tailRepeat, ''],
      ['weight_fairness', input.weights.fairness, ''],
      ['weight_phase', input.weights.phase, ''],
      ['weight_spread', input.weights.spread, ''],
      ['weight_preferred_tail', input.weights.preferredTail ?? 20, ''],
    ],
    CarryInState: Object.entries(input.carryIn.pilots)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([pid, c]) => {
        const rots = c.lastRotations;
        const prev = rots.length === 2 ? rots[0] : null;
        const last = rots.length >= 1 ? rots[rots.length - 1] : null;
        return [pid, prev?.start ?? '', prev?.tail ?? '', prev?.partner ?? '', last?.start ?? '', last?.tail ?? '', last?.partner ?? '', c.phaseOffset ?? '', JSON.stringify(c.supervisedRemaining), JSON.stringify(c.fairnessDeficit), ''];
      }),
  };
}

const CONFIG_HEADER = ['key', 'value', 'description'];
const CARRY_HEADER = [...V1_HEADERS.CarryInState, 'version_timestamp'];
// One column past the V1 layout; import reads by header prefix, so older files still load.
const PILOTS_HEADER = [...V1_HEADERS.Pilots, 'preferred_tail'];

export async function downloadExport(orgId: string): Promise<number> {
  const { input } = await fetchSnapshot(orgId);
  const stamp = new Date().toISOString().slice(0, 10);
  let n = 0;
  download(`pilot-scheduler-input-${stamp}.json`, JSON.stringify(input, null, 2), 'application/json');
  n++;
  const tabs = tabsFromInput(input);
  for (const [tab, rows] of Object.entries(tabs) as [V1Tab, unknown[][]][]) {
    const header = tab === 'Config' ? CONFIG_HEADER : tab === 'CarryInState' ? CARRY_HEADER : tab === 'Pilots' ? PILOTS_HEADER : V1_HEADERS[tab];
    await new Promise((r) => setTimeout(r, 250));
    download(`${tab}-${stamp}.csv`, toCsv(header, rows), 'text/csv');
    n++;
  }
  return n;
}
